import { price as priceUtils } from '@eti/utils';
import { css, mediaQueries } from '@eti/styles';
import { useEffect, useRef } from 'react';
import { isNoOption } from '../utils/units';
import TravelerDescription from '../../common/components/TravelerDescription';
import ProductLayout from '../../common/main-layout/ProductLayout';
import { MEAL } from '../../products';
import { getMinMealPrice, getTotalPrice, getTravelerMealPreferenceList, sortTravelers } from '../utils/mealUtil';
import { ProductCardLayoutType } from '../../common/models/product';
import NewSmallCardLayout from '../../common/components/small-card-layout/NewSmallCardLayout';
import { useProductData } from '../../common/hook/useProductData';
import SmallMealContainer from '../containers/SmallMealContainer';
import MealDropdown from './MealDropdown';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const {
  name: PRODUCT_NAME,
  id: PRODUCT_ID
} = MEAL;
const {
  Small: SMALL_CARD_LAYOUT,
  Main: MAIN_CARD_LAYOUT,
  PostBooking: POSTBOOKING_CARD_LAYOUT
} = ProductCardLayoutType;
const travelerDescriptionWrapperStyles = css`
  margin: 14px 0 10px;
`;
const mealCtaGridLayoutStyle = css`
  column-gap: 8px;
  display: grid;
  grid-template-columns: 2fr auto;
  grid-template-rows: repeat(3, auto);

  .priceLabel {
    grid-area: 1 / 1 / 1 / 3;

    @media ${mediaQueries.small.only} {
      grid-area: 1 / 1 / 1 / 3;
    }
  }

  .price {
    grid-area: 2 / 1 / 3 / 3;
    max-height: 18px;

    @media ${mediaQueries.small.only} {
      grid-area: 2 / 1 / 3 / 3;
    }
  }
`;
const sharedTexts = {
  fromPriceLabel: 'Products.Price.Label.From',
  noOptionLabel: 'Product.Meal.Dropdown.NoOption',
  readMoreContent: 'mealReadMoreModalContent',
  title: 'mealTitle',
  totalPriceLabel: 'Products.Price.Label.Total',
  travelerDescription: 'Postbooking.Traveler.Description'
};
const mainCardTranslations = {
  ...sharedTexts,
  abstractContent: 'mealAbstractText',
  upSell: 'mealUpSellText'
};
const translations = {
  [MAIN_CARD_LAYOUT]: mainCardTranslations,
  [POSTBOOKING_CARD_LAYOUT]: mainCardTranslations,
  [SMALL_CARD_LAYOUT]: {
    ...sharedTexts,
    abstractContent: 'mealAbstractSmallCard',
    upSell: 'mealUpSellTextSmallCard'
  }
};
const Meal = _ref => {
  let {
    form,
    layoutType = ProductCardLayoutType.Main,
    mealState,
    order,
    productPosition,
    travelerNames,
    updateLayoutType
  } = _ref;
  const texts = translations[layoutType] || {};
  const hasInitializedLayoutValues = useRef(false);
  const mealOrder = order;
  const isMealSelected = mealState.filter(meal => !isNoOption(meal.value)).length > 0;
  useEffect(() => {
    if (!hasInitializedLayoutValues.current) {
      updateLayoutType(layoutType);
      hasInitializedLayoutValues.current = true;
    }
  }, [layoutType, updateLayoutType]);
  const {
    productData,
    rootElement,
    translation: {
      fromPriceLabel,
      noOptionLabel,
      totalPriceLabel,
      travelerDescription
    }
  } = useProductData({
    isSelected: isMealSelected,
    productId: PRODUCT_ID,
    order: mealOrder,
    productName: PRODUCT_NAME,
    productPosition,
    form,
    texts,
    layoutType
  });
  if (!productData) {
    return null;
  }
  const travelers = getTravelerMealPreferenceList(order, travelerNames, travelerDescription, noOptionLabel);
  const mealPriceRaw = isMealSelected ? getTotalPrice(mealState) : getMinMealPrice(travelers.flatMap(traveler => traveler.mealPreferences));
  const isMultipleTravelers = travelers.length > 1;
  const sortedTravelers = isMultipleTravelers ? sortTravelers(travelers) : travelers;
  const sortedTravelersWithMeals = sortedTravelers.filter(traveler => traveler.mealPreferences.length > 0);
  const updatedProductData = {
    ...productData,
    price: priceUtils.formatPrice(mealPriceRaw),
    priceLabel: isMealSelected ? totalPriceLabel : fromPriceLabel,
    priceRaw: mealPriceRaw !== null && mealPriceRaw !== void 0 ? mealPriceRaw : 0
  };
  switch (layoutType) {
    case SMALL_CARD_LAYOUT:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: (sortedTravelersWithMeals === null || sortedTravelersWithMeals === void 0 ? void 0 : sortedTravelersWithMeals.length) > 0 && /*#__PURE__*/_jsx(NewSmallCardLayout, {
          productData: updatedProductData,
          ref: rootElement,
          children: /*#__PURE__*/_jsx(SmallMealContainer, {
            form: form,
            isMultipleTravelers: isMultipleTravelers,
            mealPriceRaw: mealPriceRaw,
            noOptionLabel: noOptionLabel,
            order: order,
            productName: PRODUCT_NAME,
            productPosition: productPosition,
            travelerDescription: travelerDescription
          })
        })
      });
    case MAIN_CARD_LAYOUT:
    default:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: (sortedTravelersWithMeals === null || sortedTravelersWithMeals === void 0 ? void 0 : sortedTravelersWithMeals.length) > 0 && /*#__PURE__*/_jsx(ProductLayout, {
          productData: updatedProductData,
          ref: rootElement,
          children: /*#__PURE__*/_jsx(ProductLayout.Cta, {
            customGrid: mealCtaGridLayoutStyle,
            children: /*#__PURE__*/_jsx("ul", {
              children: sortedTravelersWithMeals.map(traveler => /*#__PURE__*/_jsxs("li", {
                "data-testid": `${MEAL.name}-traveler-${traveler.travelerId}`,
                children: [isMultipleTravelers && /*#__PURE__*/_jsx("div", {
                  className: travelerDescriptionWrapperStyles,
                  children: /*#__PURE__*/_jsx(TravelerDescription, {
                    name: (traveler === null || traveler === void 0 ? void 0 : traveler.name) || '',
                    type: traveler === null || traveler === void 0 ? void 0 : traveler.travelerType
                  })
                }), /*#__PURE__*/_jsx(MealDropdown, {
                  currentTravelerId: traveler === null || traveler === void 0 ? void 0 : traveler.travelerId,
                  mealPreferences: (traveler === null || traveler === void 0 ? void 0 : traveler.mealPreferences) || [],
                  mealState: mealState,
                  productName: PRODUCT_NAME,
                  productPosition: productPosition,
                  rootElement: rootElement
                })]
              }, traveler.travelerId))
            })
          })
        })
      });
  }
};
export default Meal;
//# sourceMappingURL=Meal.js.map